import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle2, ArrowRight, Phone, Mail, MessageCircle } from "lucide-react";
import { motion } from "framer-motion";
import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import metaAdsCaseStudy from "@assets/meta_ads_-_ad_account_data_performance_1779180704057.jpg";

export default function FacebookAds() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 }
  };

  const features = [
    {
      title: "Improving Brand Awareness",
      description: "Increasing brand awareness on social media is important, as it can build trust and credibility. Consistent positive reactions on your Facebook and Instagram advertising can build a strong brand reputation. Introducing brand awareness into your social media strategy can help educate customers, increase followers and achieve the recognition you deserve."
    },
    {
      title: "Website Traffic",
      description: "Generating website traffic from social media should be one of your primary objectives. The Cornwall SEO Co can create a campaign that will get website traffic from Facebook and Instagram and send it to the destination of your choice. You can encourage users to call your business or show your sponsored ads on Facebook and other social media platforms to the people most likely to click them."
    },
    {
      title: "Collecting Leads",
      description: "Collecting leads through Facebook ads enables you to reach people who may be interested in your business, utilising contact details such as their name, phone number and email address. This can be received via an instant form or through your website. Lead generation on social media can be customised to help increase conversion rates."
    },
    {
      title: "Remarketing",
      description: "A major benefit of Instagram and Facebook advertising is the opportunity for remarketing on social media to existing audiences using data collected throughout your ad campaigns. You can advertise to audiences who have already visited your shop, app, website, Facebook page or Instagram account and remarket the content to retain their interest."
    }
  ];

  return (
    <div className="min-h-screen bg-background font-sans">
      <Helmet>
        <title>Facebook Advertising Agency in Cornwall | The Cornwall SEO Co.</title>
        <meta name="description" content="Working closely with clients; our highly flexible, unique strategy will help meet business goals of all shapes and sizes." />
      </Helmet>
      <Navbar />
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden pt-20 bg-black">
        <div className="container mx-auto px-6 relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <h1 className="text-5xl md:text-7xl font-heading font-black text-white mb-8 leading-none uppercase italic tracking-tighter">
              Facebook <br/>
              <span className="text-primary">Advertising</span>
            </h1>
            <p className="text-xl text-white/90 mb-10 leading-relaxed font-medium">
              Targeted audiences, flexible budgets. Maximise your Facebook and Instagram ads to reach your business goals.
            </p>
            <a href="#contact-form" className="inline-block">
              <Button size="lg" className="bg-primary text-black hover:bg-white text-lg px-10 h-16 rounded-none font-black uppercase italic transition-all cursor-pointer">
                Contact Us <ArrowRight className="ml-2" />
              </Button>
            </a>
          </motion.div>
        </div>
      </section>

      {/* USP Section */}
      <section className="py-10 bg-black border-y border-primary/20">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center divide-y md:divide-y-0 md:divide-x divide-primary/20">
            <div className="text-white font-black italic text-2xl py-4 md:py-0 px-4">100% CORNWALL-BASED TEAM</div>
            <div className="text-white font-black italic text-2xl py-4 md:py-0 px-4">NO MINIMUM TERM CONTRACT</div>
            <div className="text-white font-black italic text-2xl py-4 md:py-0 px-4">COMPETITIVE HOURLY RATE</div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <motion.div {...fadeIn} className="max-w-3xl mb-16">
            <h2 className="text-4xl md:text-6xl font-heading font-black text-black mb-6 uppercase italic tracking-tighter">
              Reach Billions <br/> <span className="text-primary">On Facebook</span>
            </h2>
            <p className="text-black text-xl font-medium leading-relaxed mb-6">
              Billions of people visit Facebook every day, making it an ideal way to target new audiences or re-engage with those who have previously shown an interest. Capitalising on Meta's advertising benefits can maximise visibility to help reach your business goals.
            </p>
            <p className="text-black text-xl font-medium leading-relaxed mb-6">
              As experts in Facebook advertising, the Cornwall SEO Co. can keep you up to date with new features and work with you on targeted ad campaigns, ensuring they comply with Meta's latest advertising policies and standards, which are in place to protect businesses and customers.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-24 bg-background">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="h-full rounded-none border-2 border-black p-6">
                  <CardHeader className="p-0 mb-4">
                    <CardTitle className="font-heading font-black text-xl md:text-xl text-black uppercase italic">{feature.title}</CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <p className="text-black font-medium leading-relaxed text-lg md:text-sm">
                      {feature.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-black text-white">
        <div className="container mx-auto px-6">
          <motion.div {...fadeIn} className="max-w-4xl mx-auto">
            <h2 className="text-4xl md:text-6xl font-heading font-black mb-10 uppercase italic tracking-tighter text-center">
              Meta Ads: <span className="text-primary">Case Study</span>
            </h2>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
              <div className="bg-white/10 p-6 text-center border-l-4 border-primary">
                <p className="text-white/70 font-bold uppercase text-sm mb-2">Ad Spend</p>
                <p className="text-3xl md:text-4xl font-black italic text-primary">£500</p>
              </div>
              <div className="bg-white/10 p-6 text-center border-l-4 border-primary">
                <p className="text-white/70 font-bold uppercase text-sm mb-2">Conversions</p>
                <p className="text-3xl md:text-4xl font-black italic text-primary">266</p>
              </div>
              <div className="bg-white/10 p-6 text-center border-l-4 border-primary">
                <p className="text-white/70 font-bold uppercase text-sm mb-2">Conversion Value</p>
                <p className="text-3xl md:text-4xl font-black italic text-primary">£27.8K</p>
              </div>
              <div className="bg-white/10 p-6 text-center border-l-4 border-primary">
                <p className="text-white/70 font-bold uppercase text-sm mb-2">ROAS</p>
                <p className="text-3xl md:text-4xl font-black italic text-primary">5570%</p>
              </div>
            </div>

            <div className="bg-primary text-black p-8 md:p-12 mb-16 text-center relative overflow-hidden">
              <div className="absolute -right-10 -top-10 text-[15rem] font-black italic opacity-10 leading-none">55x</div>
              <div className="relative z-10">
                <h3 className="text-3xl md:text-5xl font-black uppercase italic mb-8">55x Return</h3>
                <div className="flex flex-col md:flex-row items-center justify-center gap-8 md:gap-16">
                  <div>
                    <p className="font-bold text-lg mb-1 uppercase tracking-wide">Ad Spend</p>
                    <p className="text-2xl font-black italic">£500 (Investment)</p>
                  </div>
                  <ArrowRight className="hidden md:block w-12 h-12" />
                  <div>
                    <p className="font-bold text-lg mb-1 uppercase tracking-wide">Conversion Value</p>
                    <p className="text-2xl font-black italic">£27,848.48 (Return)</p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-2xl font-black uppercase italic mb-6 text-center">Screenshot from Ads Manager</h3>
              <div className="bg-white p-2 border-4 border-white/20">
                <img 
                  src={metaAdsCaseStudy} 
                  alt="Meta Ads Manager Screenshot showing 55.70 ROAS" 
                  className="w-full h-auto object-cover"
                />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <motion.div {...fadeIn} className="max-w-4xl">
            <h2 className="text-4xl md:text-6xl font-heading font-black text-black mb-10 uppercase italic tracking-tighter">
              Our <span className="text-primary">Approach</span>
            </h2>
            <p className="text-black text-xl font-medium leading-relaxed mb-8">
              Tailored to various target audiences based on demographics such as age, location, language, gender, detailed targeting, custom audiences and more, including both B2B and B2C audiences; the Cornwall SEO Co. can target your products and services to the most interested audience to elevate your overall brand visibility and improve conversions.
            </p>
            <p className="text-black text-xl font-medium leading-relaxed mb-8">
              Our ethos is to work closely with our clients, creating a flexible and unique strategy that will help businesses of all sizes, whatever they may be. With the most up to date and effective tools at our disposal, we can target locally and expand to areas outside Cornwall, depending on your business goals.
            </p>
            <p className="text-black text-xl font-medium leading-relaxed">
              To find out more about how we can use Meta ads to benefit your business, call <span className="font-black">01726 74161</span> or complete our contact form below.
            </p>
          </motion.div>
        </div>
      </section>

      <section id="contact-form" className="py-12 md:py-24 bg-primary">
        <div className="container mx-auto px-3 md:px-6">
          <div className="bg-black p-4 md:p-20 shadow-[20px_20px_0px_0px_rgba(255,255,255,0.3)] overflow-x-hidden">
            <div className="grid md:grid-cols-2 gap-4 md:gap-16">
              <div>
                <h3 className="text-3xl md:text-6xl font-heading font-black text-primary mb-4 md:mb-6 uppercase italic tracking-tighter">Ready to <br/> Grow?</h3>
                <p className="text-white/80 text-base md:text-xl mb-6 md:mb-12 font-medium">
                  Let's work together to create a Facebook advertising campaign that drives results for your business.
                </p>
                
                <div className="space-y-4 md:space-y-8">
                   <div className="flex items-center gap-3 md:gap-6">
                      <div className="w-10 h-10 md:w-12 md:h-12 bg-primary flex items-center justify-center shrink-0">
                         <Phone size={18} className="text-black md:w-6 md:h-6" />
                      </div>
                      <span className="text-white font-black uppercase italic text-sm md:text-xl break-words">01726 74161</span>
                   </div>
                   <div className="flex items-center gap-3 md:gap-6">
                      <div className="w-10 h-10 md:w-12 md:h-12 bg-primary flex items-center justify-center shrink-0">
                         <Mail size={18} className="text-black md:w-6 md:h-6" />
                      </div>
                      <a href="mailto:hello@thecornwallseoco.co.uk" className="text-white font-black uppercase italic text-xs md:text-xl hover:text-primary transition-colors break-words">hello@thecornwallseoco.co.uk</a>
                   </div>
                   <a
                     href="https://wa.me/447792646061"
                     target="_blank"
                     rel="noopener noreferrer"
                     className="flex items-center gap-3 md:gap-6 hover:text-primary transition-colors"
                   >
                      <div className="w-10 h-10 md:w-12 md:h-12 bg-primary flex items-center justify-center shrink-0">
                         <MessageCircle size={18} className="text-black md:w-6 md:h-6" />
                      </div>
                      <span className="text-white font-black uppercase italic text-sm md:text-xl hover:text-primary transition-colors">WhatsApp</span>
                   </a>
                </div>
              </div>
              
              <form action="/contact.php" method="POST" className="space-y-3 md:space-y-4">
                <Input name="name" required placeholder="NAME" className="bg-white border-none rounded-none h-12 md:h-14 font-black text-sm md:text-base placeholder:text-black/30 w-full" />
                <Input name="email" required type="email" placeholder="EMAIL" className="bg-white border-none rounded-none h-12 md:h-14 font-black text-sm md:text-base placeholder:text-black/30 w-full" />
                <Input name="website" placeholder="WEBSITE" className="bg-white border-none rounded-none h-12 md:h-14 font-black text-sm md:text-base placeholder:text-black/30 w-full" />
                <Textarea name="message" required placeholder="HOW CAN WE HELP YOU?" className="bg-white border-none rounded-none h-24 md:h-32 font-black text-sm md:text-base placeholder:text-black/30 w-full" />
                <Button type="submit" className="w-full bg-primary text-black hover:bg-white h-14 md:h-16 font-black uppercase italic text-lg md:text-xl rounded-none transition-all cursor-pointer">
                  GO
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
